import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  LogOut, Package, FileText, Users, Building2, Plus, Trash2, Edit, 
  Save, X, Upload, Eye, EyeOff, Loader2, Image, FolderOpen, GalleryHorizontal,
  ChevronUp, ChevronDown, MapPin, Newspaper, Mail, MailOpen, LayoutGrid, Bell
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { apiEndpoints, apiRequest, resolveApiAssetUrl, uploadFile } from '@/config/api';
import { Product, AboutContent, Representative, Customer, Category, ProductImage, ApiResponse, BlogPost, ContactMessage, ExecutiveDepartment, SiteSettings, CompanyCertificate } from '@/types/api';
import { isProductModelPath } from '@/components/Product3DViewer';

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  useEffect(() => { checkAuth(); }, []);

  const checkAuth = async () => {
    try {
      const response = await apiRequest<{ authenticated: boolean }>(`${apiEndpoints.login}`, {
        method: 'POST',
        body: JSON.stringify({ action: 'check' }),
      });
      setIsAuthenticated(response.authenticated);
    } catch { setIsAuthenticated(false); }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoggingIn(true);
    try {
      await apiRequest(`${apiEndpoints.login}`, {
        method: 'POST',
        body: JSON.stringify({ username, password }),
      });
      setIsAuthenticated(true);
      toast.success('ورود موفقیت‌آمیز');
    } catch (error: any) {
      toast.error(error.message || 'خطا در ورود');
    } finally { setIsLoggingIn(false); }
  };

  const handleLogout = async () => {
    try {
      await apiRequest(`${apiEndpoints.login}`, {
        method: 'POST',
        body: JSON.stringify({ action: 'logout' }),
      });
    } catch {}
    setIsAuthenticated(false);
    toast.success('خروج موفقیت‌آمیز');
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-steel-dark via-steel to-steel-light flex items-center justify-center p-4" dir="rtl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <Card className="border-copper/20 shadow-2xl">
            <CardHeader className="text-center pb-2">
              <div className="w-20 h-20 mx-auto mb-4 bg-copper/10 rounded-full flex items-center justify-center">
                <img src="/images/kep-logo.png" alt="Logo" className="w-12 h-12" />
              </div>
              <CardTitle className="text-2xl">پنل مدیریت</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <Input type="text" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="نام کاربری" className="text-right" required />
                <div className="relative">
                  <Input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="رمز عبور" className="pl-10 text-right" required />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute left-3 top-1/2 -translate-y-1/2 text-steel">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button type="submit" className="w-full bg-copper hover:bg-copper-dark" disabled={isLoggingIn}>
                  {isLoggingIn && <Loader2 className="w-4 h-4 animate-spin ml-2" />}ورود
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <header className="bg-steel-dark text-white py-4 px-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <img src="/images/kep-logo.png" alt="Logo" className="w-10 h-10" />
          <h1 className="text-xl font-bold">پنل مدیریت</h1>
        </div>
        <Button variant="ghost" onClick={handleLogout} className="text-white hover:bg-white/10">
          <LogOut className="w-4 h-4 ml-2" />خروج
        </Button>
      </header>

      <main className="container mx-auto px-4 py-8">
        <Tabs defaultValue="products" className="space-y-6" style={{direction: "rtl"}}>
          <TabsList className="bg-steel-light/50 p-1 rounded-xl flex-wrap justify-end">
            <TabsTrigger value="products" className="data-[state=active]:bg-copper data-[state=active]:text-white rounded-lg">
              <Package className="w-4 h-4 ml-2" />محصولات
            </TabsTrigger>
            <TabsTrigger value="categories" className="data-[state=active]:bg-copper data-[state=active]:text-white rounded-lg">
              <FolderOpen className="w-4 h-4 ml-2" />دسته‌بندی‌ها
            </TabsTrigger>
            <TabsTrigger value="about" className="data-[state=active]:bg-copper data-[state=active]:text-white rounded-lg">
              <FileText className="w-4 h-4 ml-2" />درباره ما
            </TabsTrigger>
            <TabsTrigger value="representatives" className="data-[state=active]:bg-copper data-[state=active]:text-white rounded-lg">
              <Building2 className="w-4 h-4 ml-2" />نمایندگان
            </TabsTrigger>
            <TabsTrigger value="executive" className="data-[state=active]:bg-copper data-[state=active]:text-white rounded-lg">
              <LayoutGrid className="w-4 h-4 ml-2" />ساختار اجرایی
            </TabsTrigger>
            <TabsTrigger value="certificates" className="data-[state=active]:bg-copper data-[state=active]:text-white rounded-lg">
              <GalleryHorizontal className="w-4 h-4 ml-2" />گواهی‌ها و مجوزها
            </TabsTrigger>
            <TabsTrigger value="customers" className="data-[state=active]:bg-copper data-[state=active]:text-white rounded-lg">
              <Users className="w-4 h-4 ml-2" />مشتریان
            </TabsTrigger>
            <TabsTrigger value="blogs" className="data-[state=active]:bg-copper data-[state=active]:text-white rounded-lg">
              <Newspaper className="w-4 h-4 ml-2" />مقالات
            </TabsTrigger>
            <TabsTrigger value="messages" className="data-[state=active]:bg-copper data-[state=active]:text-white rounded-lg">
              <Mail className="w-4 h-4 ml-2" />پیام‌ها
            </TabsTrigger>
            <TabsTrigger value="notifications" className="data-[state=active]:bg-copper data-[state=active]:text-white rounded-lg">
              <Bell className="w-4 h-4 ml-2" />اعلان بله
            </TabsTrigger>
          </TabsList>
          <TabsContent value="products"><ProductsManager /></TabsContent>
          <TabsContent value="categories"><CategoriesManager /></TabsContent>
          <TabsContent value="about"><AboutManager /></TabsContent>
          <TabsContent value="representatives"><RepresentativesManager /></TabsContent>
          <TabsContent value="executive"><ExecutiveDepartmentsManager /></TabsContent>
          <TabsContent value="certificates"><CertificatesManager /></TabsContent>
          <TabsContent value="customers"><CustomersManager /></TabsContent>
          <TabsContent value="blogs"><BlogsManager /></TabsContent>
          <TabsContent value="messages"><MessagesManager /></TabsContent>
          <TabsContent value="notifications"><BaleSettingsManager /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

// Image Upload Component
const ImageUploader = ({
  onUpload,
  currentImage,
  folder = 'products',
}: {
  onUpload: (url: string) => void;
  currentImage?: string;
  folder?: string;
}) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file, folder);
      onUpload(url);
      toast.success('تصویر آپلود شد');
    } catch (err: any) {
      toast.error(err.message || 'خطا در آپلود');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const previewSrc = resolveApiAssetUrl(currentImage) || currentImage;

  return (
    <div className="space-y-2">
      {previewSrc && (
        <img
          src={previewSrc}
          alt="پیش‌نمایش تصویر"
          className="w-32 h-32 object-cover rounded-lg border"
          loading="lazy"
        />
      )}
      <input ref={inputRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
      <Button type="button" variant="outline" onClick={() => inputRef.current?.click()} disabled={uploading}>
        {uploading ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : <Upload className="w-4 h-4 ml-2" />}
        {currentImage ? 'تغییر تصویر' : 'آپلود تصویر'}
      </Button>
    </div>
  );
};

// Multi Image Uploader for Gallery
const GalleryUploader = ({
  images,
  onImagesChange,
  productId,
}: {
  images: ProductImage[];
  onImagesChange: (images: ProductImage[]) => void;
  productId?: number;
}) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const newImages: ProductImage[] = [];
      for (let i = 0; i < files.length; i++) {
        const url = await uploadFile(files[i]);
        const newImage: ProductImage = {
          id: Date.now() + i,
          product_id: productId || 0,
          image_url: url,
          alt_text: '',
          sort_order: images.length + i,
        };
        newImages.push(newImage);
      }
      onImagesChange([...images, ...newImages]);
      toast.success(`${files.length} تصویر آپلود شد`);
    } catch (err: any) {
      toast.error(err.message || 'خطا در آپلود');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const removeImage = (index: number) => {
    const newImages = images.filter((_, i) => i !== index);
    onImagesChange(newImages);
  };

  const moveImage = (index: number, direction: 'up' | 'down') => {
    const newImages = [...images];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= images.length) return;
    [newImages[index], newImages[newIndex]] = [newImages[newIndex], newImages[index]];
    newImages.forEach((img, i) => (img.sort_order = i));
    onImagesChange(newImages);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium">گالری تصاویر</label>
        <input ref={inputRef} type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" />
        <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={uploading}>
          {uploading ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : <Plus className="w-4 h-4 ml-2" />}
          افزودن تصویر
        </Button>
      </div>

      {images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {images.map((image, index) => {
            const src = resolveApiAssetUrl(image.image_url) || image.image_url;
            return (
              <div key={image.id || index} className="relative group">
                <img
                  src={src}
                  alt={image.alt_text || `تصویر ${index + 1}`}
                  className="w-full h-24 object-cover rounded-lg border"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-white hover:bg-white/20"
                    onClick={() => moveImage(index, 'up')}
                    disabled={index === 0}
                  >
                    <ChevronUp className="w-4 h-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-white hover:bg-white/20"
                    onClick={() => moveImage(index, 'down')}
                    disabled={index === images.length - 1}
                  >
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-white hover:bg-red-500/80"
                    onClick={() => removeImage(index)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {images.length === 0 && (
        <div className="border-2 border-dashed border-steel-light/50 rounded-lg p-8 text-center">
          <Image className="w-12 h-12 text-steel/30 mx-auto mb-2" />
          <p className="text-steel text-sm">هنوز تصویری اضافه نشده</p>
        </div>
      )}
    </div>
  );
};

// Categories Manager
const CategoriesManager = () => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Category | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({ name: '', slug: '', description: '', icon: 'Folder', is_active: true });

  const { data, isLoading } = useQuery({
    queryKey: ['admin-categories'],
    queryFn: () => apiRequest<ApiResponse<Category[]>>(`${apiEndpoints.categories}?active=false`),
  });

  const saveMutation = useMutation({
    mutationFn: (data: any) => apiRequest(apiEndpoints.categories, {
      method: editing ? 'PUT' : 'POST',
      body: JSON.stringify(editing ? { ...data, id: editing.id } : data),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] });
      toast.success(editing ? 'به‌روزرسانی شد' : 'ایجاد شد');
      setEditing(null); setIsCreating(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest(`${apiEndpoints.categories}?id=${id}`, { method: 'DELETE' }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['admin-categories'] }); toast.success('حذف شد'); },
  });

  useEffect(() => {
    if (editing) setFormData({ name: editing.name, slug: editing.slug, description: editing.description || '', icon: editing.icon, is_active: editing.is_active });
    else setFormData({ name: '', slug: '', description: '', icon: 'Folder', is_active: true });
  }, [editing]);

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-copper" /></div>;

  if (editing || isCreating) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{editing ? 'ویرایش دسته‌بندی' : 'افزودن دسته‌بندی'}</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => { setEditing(null); setIsCreating(false); }}><X className="w-4 h-4" /></Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={(e) => { e.preventDefault(); saveMutation.mutate(formData); }} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <Input placeholder="نام دسته‌بندی" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="text-right" required />
              <Input placeholder="نامک (slug)" value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} dir="ltr" className="text-left" />
            </div>
            <Textarea placeholder="توضیحات" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="text-right" />
            <Input placeholder="آیکون (Zap, Cog, Settings, Wrench, Cpu)" value={formData.icon} onChange={(e) => setFormData({ ...formData, icon: e.target.value })} className="text-right" />
            <Button type="submit" className="bg-copper hover:bg-copper-dark" disabled={saveMutation.isPending}>
              {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : <Save className="w-4 h-4 ml-2" />}ذخیره
            </Button>
          </form>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>دسته‌بندی‌ها</CardTitle>
        <Button onClick={() => setIsCreating(true)} className="bg-copper hover:bg-copper-dark"><Plus className="w-4 h-4 ml-2" />افزودن</Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {(data?.data || []).map((cat) => (
            <div key={cat.id} className="flex items-center justify-between p-4 bg-steel-light/30 rounded-lg">
              <div><h3 className="font-bold">{cat.name}</h3><p className="text-sm text-steel">{cat.product_count || 0} محصول</p></div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setEditing(cat)}><Edit className="w-4 h-4" /></Button>
                <Button variant="destructive" size="sm" onClick={() => confirm('حذف شود؟') && deleteMutation.mutate(cat.id)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// Products Manager
const ProductsManager = () => {
  const queryClient = useQueryClient();
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const { data: productsData, isLoading } = useQuery({
    queryKey: ['admin-products'],
    queryFn: () => apiRequest<ApiResponse<Product[]>>(`${apiEndpoints.products}?active=false`),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest(`${apiEndpoints.products}?id=${id}`, { method: 'DELETE' }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['admin-products'] }); toast.success('محصول حذف شد'); },
  });

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-copper" /></div>;
  if (editingProduct || isCreating) return <ProductForm product={editingProduct} onClose={() => { setEditingProduct(null); setIsCreating(false); }} />;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>مدیریت محصولات</CardTitle>
        <Button onClick={() => setIsCreating(true)} className="bg-copper hover:bg-copper-dark"><Plus className="w-4 h-4 ml-2" />افزودن محصول</Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {(productsData?.data || []).map((product) => (
            <div key={product.id} className="flex items-center justify-between p-4 bg-steel-light/30 rounded-lg">
              <div className="flex items-center gap-4">
                <img
                  src={
                    resolveApiAssetUrl(product.main_image || product.images?.[0]?.image_url) ||
                    '/images/electric-motor.jpg'
                  }
                  alt={product.title}
                  className="w-16 h-16 object-cover rounded-lg"
                  loading="lazy"
                />
                <div>
                  <h3 className="font-bold">{product.title}</h3>
                  <p className="text-sm text-steel">{product.category_name || 'بدون دسته‌بندی'}</p>
                  <div className="flex gap-2 mt-1">
                    <span className={`text-xs px-2 py-0.5 rounded ${product.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{product.is_active ? 'فعال' : 'غیرفعال'}</span>
                    {product.show_in_gallery && <span className="text-xs px-2 py-0.5 rounded bg-copper/20 text-copper flex items-center gap-1"><GalleryHorizontal className="w-3 h-3" />گالری</span>}
                    {product.images && product.images.length > 0 && (
                      <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700 flex items-center gap-1">
                        <Image className="w-3 h-3" />{product.images.length} عکس
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setEditingProduct(product)}><Edit className="w-4 h-4" /></Button>
                <Button variant="destructive" size="sm" onClick={() => confirm('حذف شود؟') && deleteMutation.mutate(product.id)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// Product Form
const ProductForm = ({ product, onClose }: { product: Product | null; onClose: () => void }) => {
  const queryClient = useQueryClient();
  const { data: categoriesData } = useQuery({ queryKey: ['categories'], queryFn: () => apiRequest<ApiResponse<Category[]>>(`${apiEndpoints.categories}?active=false`) });
  
  const [formData, setFormData] = useState({
    title: product?.title || '', slug: product?.slug || '', description: product?.description || '',
    full_description: product?.full_description || '', icon: product?.icon || 'Zap', is_active: product?.is_active ?? true,
    main_image: product?.main_image || '', category_id: product?.category_id || '',
    model_3d_enabled: Boolean(product?.model_3d_enabled), model_3d_url: product?.model_3d_url || '',
    show_in_gallery: product?.show_in_gallery ?? false, gallery_order: product?.gallery_order || 0,
    specifications: product?.specifications || {},
  });
  const [galleryImages, setGalleryImages] = useState<ProductImage[]>(product?.images || []);
  const [specKey, setSpecKey] = useState(''); const [specValue, setSpecValue] = useState('');

  const mutation = useMutation({
    mutationFn: (data: any) => apiRequest(apiEndpoints.products, { 
      method: product ? 'PUT' : 'POST', 
      body: JSON.stringify(product ? { ...data, id: product.id } : data) 
    }),
    onSuccess: () => { 
      queryClient.invalidateQueries({ queryKey: ['admin-products'] }); 
      queryClient.invalidateQueries({ queryKey: ['product'] });
      toast.success(product ? 'به‌روزرسانی شد' : 'ایجاد شد'); 
      onClose(); 
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.model_3d_enabled && !isProductModelPath(formData.model_3d_url)) { toast.error('مسیر نمایشگر سه‌بعدی معتبر را وارد کنید؛ مانند /models/cooleri-p14/index.html'); return; }
    const submitData = {
      ...formData,
      images: galleryImages.map((img, index) => ({
        image_url: img.image_url,
        alt_text: img.alt_text || formData.title,
        sort_order: index,
      })),
    };
    mutation.mutate(submitData);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{product ? 'ویرایش محصول' : 'افزودن محصول'}</CardTitle>
        <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <Input placeholder="عنوان" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="text-right" required />
            <Input placeholder="نامک (slug)" value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} dir="ltr" className="text-left" />
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <select value={formData.category_id} onChange={(e) => setFormData({ ...formData, category_id: e.target.value })} className="flex h-10 w-full rounded-md border px-3 py-2 text-sm bg-background text-right">
              <option value="">بدون دسته‌بندی</option>
              {(categoriesData?.data || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <Input placeholder="آیکون" value={formData.icon} onChange={(e) => setFormData({ ...formData, icon: e.target.value })} className="text-right" />
          </div>

          <Textarea placeholder="توضیحات کوتاه (نمایش در لیست محصولات)" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} className="text-right" />
          <Textarea placeholder="توضیحات کامل (نمایش در صفحه محصول)" value={formData.full_description} onChange={(e) => setFormData({ ...formData, full_description: e.target.value })} rows={6} className="text-right" />

          {/* Main Image */}
          <div className="space-y-2">
            <label className="block text-sm font-medium">عکس اصلی محصول</label>
            <div className="flex flex-wrap items-start gap-3">
              <ImageUploader
                currentImage={formData.main_image || undefined}
                onUpload={(url) => setFormData({ ...formData, main_image: url })}
              />
              {formData.main_image && (
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={() => setFormData({ ...formData, main_image: '' })}
                >
                  <Trash2 className="w-4 h-4 ml-2" />
                  حذف عکس اصلی
                </Button>
              )}
            </div>
          </div>

          {/* Gallery Images */}
          <div className="space-y-3 rounded-xl border border-copper/30 bg-copper/5 p-4">
            <label className="flex cursor-pointer items-center gap-2 text-sm font-bold"><input type="checkbox" checked={formData.model_3d_enabled} onChange={(e) => setFormData({ ...formData, model_3d_enabled: e.target.checked })} className="h-4 w-4 accent-[hsl(var(--copper))]" />فعال‌سازی نمایش سه‌بعدی محصول</label>
            <label className="block text-xs text-muted-foreground" htmlFor="model-3d-url">مسیر بسته نمایشگر این محصول در سایت</label>
            <Input id="model-3d-url" value={formData.model_3d_url} onChange={(e) => setFormData({ ...formData, model_3d_url: e.target.value.trim() })} placeholder="/models/cooleri-p14/index.html" dir="ltr" aria-label="مسیر نمایشگر سه‌بعدی" />
            <p className="text-xs leading-6 text-muted-foreground">با فعال کردن این گزینه و ذخیره، دکمه سه‌بعدی کنار تصاویر همین محصول نمایش داده می‌شود. غیرفعال‌سازی، مدل ذخیره‌شده را حذف نمی‌کند.</p>
            {isProductModelPath(formData.model_3d_url) && <a href={formData.model_3d_url} target="_blank" rel="noopener noreferrer" className="inline-block text-xs font-bold text-copper underline">پیش‌نمایش مدل</a>}
          </div>
          <GalleryUploader 
            images={galleryImages} 
            onImagesChange={setGalleryImages}
            productId={product?.id}
          />

          <div>
            <label className="block text-sm font-medium mb-2">مشخصات فنی</label>
            <div className="flex gap-2 mb-3">
              <Input placeholder="نام" value={specKey} onChange={(e) => setSpecKey(e.target.value)} className="text-right" />
              <Input placeholder="مقدار" value={specValue} onChange={(e) => setSpecValue(e.target.value)} className="text-right" />
              <Button type="button" onClick={() => { if (specKey && specValue) { setFormData({ ...formData, specifications: { ...formData.specifications, [specKey]: specValue } }); setSpecKey(''); setSpecValue(''); } }} className="bg-copper"><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="space-y-2">
              {Object.entries(formData.specifications).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between bg-steel-light/30 p-2 rounded">
                  <span><strong>{k}:</strong> {v}</span>
                  <Button type="button" variant="ghost" size="sm" onClick={() => { const s = { ...formData.specifications }; delete s[k]; setFormData({ ...formData, specifications: s }); }}><X className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} className="w-4 h-4" />فعال</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.show_in_gallery} onChange={(e) => setFormData({ ...formData, show_in_gallery: e.target.checked })} className="w-4 h-4" />نمایش در گالری تولید</label>
            {formData.show_in_gallery && <Input type="number" placeholder="ترتیب گالری" value={formData.gallery_order} onChange={(e) => setFormData({ ...formData, gallery_order: parseInt(e.target.value) || 0 })} className="w-32 text-right" />}
          </div>

          <div className="flex gap-3">
            <Button type="submit" className="bg-copper hover:bg-copper-dark" disabled={mutation.isPending}>{mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : <Save className="w-4 h-4 ml-2" />}ذخیره</Button>
            <Button type="button" variant="outline" onClick={onClose}>انصراف</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// About, Representatives, Customers Managers
const AboutManager = () => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<Partial<AboutContent>>({});
  const { data, isLoading } = useQuery({ queryKey: ['about'], queryFn: () => apiRequest<ApiResponse<AboutContent>>(apiEndpoints.about) });
  useEffect(() => { if (data?.data) setFormData(data.data); }, [data]);
  const mutation = useMutation({ mutationFn: (d: Partial<AboutContent>) => apiRequest(apiEndpoints.about, { method: 'PUT', body: JSON.stringify(d) }), onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['about'] }); toast.success('ذخیره شد'); } });
  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-copper" /></div>;
  return (
    <Card><CardHeader><CardTitle>درباره ما</CardTitle></CardHeader><CardContent>
      <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(formData); }} className="space-y-4">
        <Input placeholder="عنوان" value={formData.title || ''} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="text-right" />
        <Textarea placeholder="محتوا" value={formData.content || ''} onChange={(e) => setFormData({ ...formData, content: e.target.value })} rows={5} className="text-right" />
        <Textarea placeholder="چشم‌انداز" value={formData.vision || ''} onChange={(e) => setFormData({ ...formData, vision: e.target.value })} rows={3} className="text-right" />
        <Textarea placeholder="ماموریت" value={formData.mission || ''} onChange={(e) => setFormData({ ...formData, mission: e.target.value })} rows={3} className="text-right" />
        <Textarea placeholder="تاریخچه" value={formData.history || ''} onChange={(e) => setFormData({ ...formData, history: e.target.value })} rows={3} className="text-right" />
        <Button type="submit" className="bg-copper" disabled={mutation.isPending}>{mutation.isPending && <Loader2 className="w-4 h-4 animate-spin ml-2" />}<Save className="w-4 h-4 ml-2" />ذخیره</Button>
      </form>
    </CardContent></Card>
  );
};

const RepresentativesManager = () => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Representative | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ['representatives'], queryFn: () => apiRequest<ApiResponse<Representative[]>>(`${apiEndpoints.representatives}?active=false`) });
  const deleteMutation = useMutation({ mutationFn: (id: number) => apiRequest(`${apiEndpoints.representatives}?id=${id}`, { method: 'DELETE' }), onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['representatives'] }); toast.success('حذف شد'); } });
  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-copper" /></div>;
  if (editing || isCreating) return <RepForm rep={editing} onClose={() => { setEditing(null); setIsCreating(false); }} />;
  return (
    <Card><CardHeader className="flex flex-row items-center justify-between"><CardTitle>نمایندگان</CardTitle><Button onClick={() => setIsCreating(true)} className="bg-copper"><Plus className="w-4 h-4 ml-2" />افزودن</Button></CardHeader><CardContent>
      <div className="p-4 mb-4 bg-copper/10 rounded-lg text-sm text-steel-dark">
        <MapPin className="w-4 h-4 inline-block ml-2 text-copper" />
        نمایندگان بر اساس نام شهر روی نقشه صفحه اصلی نمایش داده می‌شوند.
      </div>
      <div className="space-y-3">{(data?.data || []).map((r) => (
        <div key={r.id} className="flex items-center justify-between p-4 bg-steel-light/30 rounded-lg">
          <div>
            <h3 className="font-bold">{r.name}</h3>
            <p className="text-sm text-steel">{r.city} - {r.province}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditing(r)}><Edit className="w-4 h-4" /></Button>
            <Button variant="destructive" size="sm" onClick={() => confirm('حذف؟') && deleteMutation.mutate(r.id)}><Trash2 className="w-4 h-4" /></Button>
          </div>
        </div>
      ))}</div>
    </CardContent></Card>
  );
};

const RepForm = ({ rep, onClose }: { rep: Representative | null; onClose: () => void }) => {
  const queryClient = useQueryClient();
  const [f, setF] = useState({ 
    name: rep?.name || '', 
    city: rep?.city || '', 
    province: rep?.province || '', 
    address: rep?.address || '', 
    phone: rep?.phone || '', 
    mobile: rep?.mobile || '', 
    email: rep?.email || '', 
    latitude: rep?.latitude || null,
    longitude: rep?.longitude || null,
    is_active: rep?.is_active ?? true 
  });
  const mutation = useMutation({ mutationFn: (d: any) => apiRequest(apiEndpoints.representatives, { method: rep ? 'PUT' : 'POST', body: JSON.stringify(rep ? { ...d, id: rep.id } : d) }), onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['representatives'] }); toast.success('ذخیره شد'); onClose(); } });
  
  const cities = ['تهران', 'اصفهان', 'مشهد', 'تبریز', 'شیراز', 'کرج', 'قم', 'اهواز', 'کرمانشاه', 'ارومیه', 'رشت', 'زاهدان', 'همدان', 'کرمان', 'یزد', 'اردبیل', 'بندرعباس', 'اراک', 'زنجان', 'سنندج', 'قزوین', 'خرم‌آباد', 'گرگان', 'ساری', 'بوشهر', 'بجنورد', 'بیرجند', 'ایلام', 'سمنان', 'شهرکرد', 'یاسوج'];
  
  return (
    <Card><CardHeader className="flex flex-row items-center justify-between"><CardTitle>{rep ? 'ویرایش نماینده' : 'افزودن نماینده'}</CardTitle><Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button></CardHeader><CardContent>
      <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(f); }} className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <Input placeholder="نام نماینده" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="text-right" required />
          <select value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className="flex h-10 w-full rounded-md border px-3 py-2 text-sm bg-background text-right" required>
            <option value="">انتخاب شهر</option>
            {cities.map(city => <option key={city} value={city}>{city}</option>)}
          </select>
          <Input placeholder="استان" value={f.province} onChange={(e) => setF({ ...f, province: e.target.value })} className="text-right" />
          <Input placeholder="تلفن" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="text-right" dir="ltr" />
          <Input placeholder="موبایل" value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} className="text-right" dir="ltr" />
          <Input placeholder="ایمیل" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="text-left" dir="ltr" />
        </div>
        <Textarea placeholder="آدرس" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} className="text-right" />
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={f.is_active} onChange={(e) => setF({ ...f, is_active: e.target.checked })} className="w-4 h-4" />
          فعال
        </label>
        <Button type="submit" className="bg-copper" disabled={mutation.isPending}>{mutation.isPending && <Loader2 className="w-4 h-4 animate-spin ml-2" />}<Save className="w-4 h-4 ml-2" />ذخیره</Button>
      </form>
    </CardContent></Card>
  );
};

const CustomersManager = () => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Customer | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ['customers'], queryFn: () => apiRequest<ApiResponse<Customer[]>>(`${apiEndpoints.customers}?active=false`) });
  const deleteMutation = useMutation({ mutationFn: (id: number) => apiRequest(`${apiEndpoints.customers}?id=${id}`, { method: 'DELETE' }), onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['customers'] }); toast.success('حذف شد'); } });
  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-copper" /></div>;
  if (editing || isCreating) return <CustomerForm customer={editing} onClose={() => { setEditing(null); setIsCreating(false); }} />;
  return (
    <Card><CardHeader className="flex flex-row items-center justify-between"><CardTitle>مشتریان</CardTitle><Button onClick={() => setIsCreating(true)} className="bg-copper"><Plus className="w-4 h-4 ml-2" />افزودن</Button></CardHeader><CardContent>
      <div className="space-y-3">{(data?.data || []).map((c) => (
        <div key={c.id} className="flex items-center justify-between p-4 bg-steel-light/30 rounded-lg">
          <div className="flex items-center gap-3">
            {c.logo && <img src={c.logo} alt={c.name} className="w-12 h-12 object-contain" />}
            <div><h3 className="font-bold">{c.name}</h3><p className="text-sm text-steel">{c.description}</p></div>
          </div>
          <div className="flex gap-2"><Button variant="outline" size="sm" onClick={() => setEditing(c)}><Edit className="w-4 h-4" /></Button><Button variant="destructive" size="sm" onClick={() => confirm('حذف؟') && deleteMutation.mutate(c.id)}><Trash2 className="w-4 h-4" /></Button></div>
        </div>
      ))}</div>
    </CardContent></Card>
  );
};

const CustomerForm = ({ customer, onClose }: { customer: Customer | null; onClose: () => void }) => {
  const queryClient = useQueryClient();
  const [f, setF] = useState({ name: customer?.name || '', website: customer?.website || '', description: customer?.description || '', logo: customer?.logo || '', is_active: customer?.is_active ?? true });
  const mutation = useMutation({ mutationFn: (d: any) => apiRequest(apiEndpoints.customers, { method: customer ? 'PUT' : 'POST', body: JSON.stringify(customer ? { ...d, id: customer.id } : d) }), onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['customers'] }); toast.success('ذخیره شد'); onClose(); } });
  return (
    <Card><CardHeader className="flex flex-row items-center justify-between"><CardTitle>{customer ? 'ویرایش مشتری' : 'افزودن مشتری'}</CardTitle><Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button></CardHeader><CardContent>
      <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(f); }} className="space-y-4">
        <Input placeholder="نام" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="text-right" required />
        <Input placeholder="وبسایت" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} className="text-left" dir="ltr" />
        <Textarea placeholder="توضیحات" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="text-right" />
        <div><label className="block text-sm font-medium mb-2">لوگو</label><ImageUploader currentImage={f.logo || undefined} onUpload={(url) => setF({ ...f, logo: url })} /></div>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={f.is_active} onChange={(e) => setF({ ...f, is_active: e.target.checked })} className="w-4 h-4" />
          فعال
        </label>
        <Button type="submit" className="bg-copper" disabled={mutation.isPending}>{mutation.isPending && <Loader2 className="w-4 h-4 animate-spin ml-2" />}<Save className="w-4 h-4 ml-2" />ذخیره</Button>
      </form>
    </CardContent></Card>
  );
};

const ExecutiveDepartmentsManager = () => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<ExecutiveDepartment | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['admin-executive-sections'],
    queryFn: () => apiRequest<ApiResponse<ExecutiveDepartment[]>>(`${apiEndpoints.executiveSections}?active=false`),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest(`${apiEndpoints.executiveSections}?id=${id}`, { method: 'DELETE' }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['admin-executive-sections'] }); queryClient.invalidateQueries({ queryKey: ['executive-sections'] }); toast.success('کاشی حذف شد'); },
  });
  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-copper" /></div>;
  if (editing || isCreating) return <ExecutiveDepartmentForm department={editing} onClose={() => { setEditing(null); setIsCreating(false); }} />;
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between"><div><CardTitle>ساختار اجرایی و معرفی مدیران</CardTitle><p className="mt-2 text-sm text-steel">هر کاشی یک پاپ‌آپ اختصاصی برای عکس، نام و معرفی مدیر خود دارد.</p></div><Button onClick={() => setIsCreating(true)} className="bg-copper"><Plus className="ml-2 h-4 w-4" />افزودن کاشی</Button></CardHeader>
      <CardContent><div className="space-y-3">{(data?.data || []).map((department) => (
        <div key={department.id} className="flex items-center justify-between gap-4 rounded-xl bg-steel-light/30 p-4">
          <div className="flex min-w-0 items-center gap-3"><div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-copper/10 text-copper"><LayoutGrid className="h-5 w-5" /></div><div className="min-w-0"><h3 className="font-bold">{department.title}</h3><p className="truncate text-sm text-steel">{department.person_first_name || department.person_last_name ? `${department.person_first_name} ${department.person_last_name}` : 'مدیر تعریف نشده'} · {department.description}</p></div></div>
          <div className="flex shrink-0 gap-2"><Button variant="outline" size="sm" onClick={() => setEditing(department)}><Edit className="h-4 w-4" /></Button><Button variant="destructive" size="sm" onClick={() => confirm('این کاشی حذف شود؟') && deleteMutation.mutate(department.id)}><Trash2 className="h-4 w-4" /></Button></div>
        </div>
      ))}{(data?.data || []).length === 0 && <p className="py-8 text-center text-steel">هنوز کاشی‌ای ثبت نشده است.</p>}</div></CardContent>
    </Card>
  );
};

const ExecutiveDepartmentForm = ({ department, onClose }: { department: ExecutiveDepartment | null; onClose: () => void }) => {
  const queryClient = useQueryClient();
  const [f, setF] = useState({
    title: department?.title || '', description: department?.description || '', icon: department?.icon || 'Factory',
    person_first_name: department?.person_first_name || '', person_last_name: department?.person_last_name || '',
    person_role: department?.person_role || '', person_bio: department?.person_bio || '', person_image: department?.person_image || '',
    is_active: department?.is_active ?? true, sort_order: department?.sort_order || 0,
  });
  const mutation = useMutation({
    mutationFn: (payload: typeof f) => apiRequest(apiEndpoints.executiveSections, { method: department ? 'PUT' : 'POST', body: JSON.stringify(department ? { ...payload, id: department.id } : payload) }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['admin-executive-sections'] }); queryClient.invalidateQueries({ queryKey: ['executive-sections'] }); toast.success('کاشی با موفقیت ذخیره شد'); onClose(); },
  });
  return (
    <Card><CardHeader className="flex flex-row items-center justify-between"><div><CardTitle>{department ? 'ویرایش کاشی و مدیر' : 'افزودن کاشی و مدیر'}</CardTitle><p className="mt-1 text-sm text-steel">اطلاعاتی که در پاپ‌آپ این کاشی نمایش داده می‌شود را وارد کنید.</p></div><Button variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4" /></Button></CardHeader><CardContent>
      <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(f); }} className="space-y-5">
        <div className="grid gap-4 md:grid-cols-2"><Input placeholder="نام بخش *" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="text-right" required /><Input placeholder="توضیح کوتاه بخش" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="text-right" /><select value={f.icon} onChange={(e) => setF({ ...f, icon: e.target.value })} className="flex h-10 w-full rounded-md border bg-background px-3 py-2 text-right text-sm"><option value="Factory">تولید</option><option value="Shield">کنترل کیفیت</option><option value="Users">تحقیق و توسعه</option><option value="Truck">فروش و توزیع</option><option value="Settings">خدمات فنی</option><option value="Award">تضمین کیفیت</option></select><Input type="number" placeholder="ترتیب نمایش" value={f.sort_order} onChange={(e) => setF({ ...f, sort_order: Number(e.target.value) })} dir="ltr" /></div>
        <div className="rounded-2xl border border-copper/20 bg-copper/5 p-4"><h3 className="mb-4 font-bold text-copper">اطلاعات مدیر این بخش</h3><div className="grid gap-4 md:grid-cols-2"><Input placeholder="نام مدیر" value={f.person_first_name} onChange={(e) => setF({ ...f, person_first_name: e.target.value })} className="text-right" /><Input placeholder="نام خانوادگی مدیر" value={f.person_last_name} onChange={(e) => setF({ ...f, person_last_name: e.target.value })} className="text-right" /><Input placeholder="سمت سازمانی" value={f.person_role} onChange={(e) => setF({ ...f, person_role: e.target.value })} className="text-right md:col-span-2" /><Textarea placeholder="معرفی و توضیح درباره مدیر" value={f.person_bio} onChange={(e) => setF({ ...f, person_bio: e.target.value })} rows={5} className="text-right md:col-span-2" /></div><div className="mt-4"><label className="mb-2 block text-sm font-medium">عکس مدیر</label><ImageUploader folder="executive" currentImage={f.person_image || undefined} onUpload={(url) => setF({ ...f, person_image: url })} /></div></div>
        <label className="flex items-center gap-2"><input type="checkbox" checked={f.is_active} onChange={(e) => setF({ ...f, is_active: e.target.checked })} className="h-4 w-4" />نمایش در سایت</label>
        <div className="flex gap-3"><Button type="submit" className="bg-copper" disabled={mutation.isPending}>{mutation.isPending && <Loader2 className="ml-2 h-4 w-4 animate-spin" />}<Save className="ml-2 h-4 w-4" />ذخیره</Button><Button type="button" variant="outline" onClick={onClose}>انصراف</Button></div>
      </form>
    </CardContent></Card>
  );
};

const CertificatesManager = () => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<CompanyCertificate | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['admin-certificates'],
    queryFn: () => apiRequest<ApiResponse<CompanyCertificate[]>>(`${apiEndpoints.certificates}?active=false`),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest(`${apiEndpoints.certificates}?id=${id}`, { method: 'DELETE' }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['admin-certificates'] }); queryClient.invalidateQueries({ queryKey: ['company-certificates'] }); toast.success('گواهی حذف شد'); },
  });
  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-copper" /></div>;
  if (editing || isCreating) return <CertificateForm certificate={editing} onClose={() => { setEditing(null); setIsCreating(false); }} />;
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between"><div><CardTitle>گواهی‌ها و مجوزهای رسمی</CardTitle><p className="mt-2 text-sm text-steel">تصاویر گواهی‌ها در سکشن درباره شرکت به‌صورت اسلاید خودکار نمایش داده می‌شوند. ترتیب، عنوان و وضعیت نمایش هر مورد قابل ویرایش است.</p></div><Button onClick={() => setIsCreating(true)} className="bg-copper"><Plus className="ml-2 h-4 w-4" />افزودن گواهی</Button></CardHeader>
      <CardContent><div className="space-y-3">{(data?.data || []).map((certificate) => (
        <div key={certificate.id} className="flex flex-col gap-4 rounded-xl bg-steel-light/30 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-3"><div className="flex h-16 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white"><img src={resolveApiAssetUrl(certificate.image_url) || ''} alt={certificate.title} className="h-full w-full object-contain" /></div><div className="min-w-0"><h3 className="font-bold">{certificate.title}</h3><p className="truncate text-sm text-steel">ترتیب: {certificate.sort_order} · {certificate.is_active ? 'نمایش در سایت' : 'مخفی'}</p></div></div>
          <div className="flex shrink-0 gap-2"><Button variant="outline" size="sm" onClick={() => setEditing(certificate)}><Edit className="h-4 w-4" /></Button><Button variant="destructive" size="sm" onClick={() => confirm('این گواهی حذف شود؟') && deleteMutation.mutate(certificate.id)}><Trash2 className="h-4 w-4" /></Button></div>
        </div>
      ))}{(data?.data || []).length === 0 && <p className="py-8 text-center text-steel">هنوز گواهی‌ای ثبت نشده است.</p>}</div></CardContent>
    </Card>
  );
};

const CertificateForm = ({ certificate, onClose }: { certificate: CompanyCertificate | null; onClose: () => void }) => {
  const queryClient = useQueryClient();
  const [f, setF] = useState({ title: certificate?.title || '', description: certificate?.description || '', image_url: certificate?.image_url || '', sort_order: certificate?.sort_order || 0, is_active: certificate?.is_active ?? true });
  const mutation = useMutation({
    mutationFn: (payload: typeof f) => apiRequest(apiEndpoints.certificates, { method: certificate ? 'PUT' : 'POST', body: JSON.stringify(certificate ? { ...payload, id: certificate.id } : payload) }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['admin-certificates'] }); queryClient.invalidateQueries({ queryKey: ['company-certificates'] }); toast.success('گواهی با موفقیت ذخیره شد'); onClose(); },
  });
  return (
    <Card><CardHeader className="flex flex-row items-center justify-between"><div><CardTitle>{certificate ? 'ویرایش گواهی' : 'افزودن گواهی جدید'}</CardTitle><p className="mt-1 text-sm text-steel">برای خوانایی سند، تصویر با نسبت اصلی و بدون برش نمایش داده می‌شود.</p></div><Button variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4" /></Button></CardHeader><CardContent>
      <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(f); }} className="space-y-5">
        <div className="grid gap-4 md:grid-cols-2"><Input placeholder="عنوان گواهی *" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="text-right" required /><Input type="number" placeholder="ترتیب نمایش" value={f.sort_order} onChange={(e) => setF({ ...f, sort_order: Number(e.target.value) })} dir="ltr" /></div>
        <Textarea placeholder="توضیح کوتاه درباره گواهی (اختیاری)" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={3} className="text-right" />
        <div><label className="mb-2 block text-sm font-medium">تصویر گواهی *</label><ImageUploader folder="certificates" currentImage={f.image_url || undefined} onUpload={(url) => setF({ ...f, image_url: url })} /></div>
        <label className="flex items-center gap-2"><input type="checkbox" checked={f.is_active} onChange={(e) => setF({ ...f, is_active: e.target.checked })} className="h-4 w-4" />نمایش در سایت</label>
        <div className="flex gap-3"><Button type="submit" className="bg-copper" disabled={mutation.isPending || !f.image_url}>{mutation.isPending && <Loader2 className="ml-2 h-4 w-4 animate-spin" />}<Save className="ml-2 h-4 w-4" />ذخیره</Button><Button type="button" variant="outline" onClick={onClose}>انصراف</Button></div>
      </form>
    </CardContent></Card>
  );
};

const BaleSettingsManager = () => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ enabled: false, token: '', recipients: '', tokenConfigured: false });
  const { data, isLoading } = useQuery({ queryKey: ['site-settings'], queryFn: () => apiRequest<ApiResponse<SiteSettings>>(apiEndpoints.settings) });
  useEffect(() => { if (data?.data) setForm({ enabled: Boolean(data.data.bale_enabled), token: '', recipients: (data.data.bale_recipients || []).join('\n'), tokenConfigured: Boolean(data.data.bale_token_configured) }); }, [data]);
  const mutation = useMutation({ mutationFn: () => apiRequest(apiEndpoints.settings, { method: 'PUT', body: JSON.stringify({ bale_enabled: form.enabled, bale_token: form.token, bale_recipients: form.recipients.split(/\r?\n/).map((item) => item.trim()).filter(Boolean) }) }), onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['site-settings'] }); toast.success('تنظیمات بله ذخیره شد'); setForm((current) => ({ ...current, token: '', tokenConfigured: true })); } });
  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-copper" /></div>;
  return <Card><CardHeader><CardTitle>اعلان پیام‌های جدید در بله</CardTitle><p className="mt-2 text-sm leading-7 text-steel">بعد از ثبت فرم تماس، متن پیام و مشخصات ارسال‌کننده برای همه شناسه‌های چت ثبت‌شده ارسال می‌شود.</p></CardHeader><CardContent><form onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="max-w-2xl space-y-5"><label className="flex items-center gap-2"><input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="h-4 w-4" />ارسال اعلان بله فعال باشد</label><div><label className="mb-2 block text-sm font-medium">توکن ربات بله</label><Input type="password" value={form.token} onChange={(e) => setForm({ ...form, token: e.target.value })} placeholder={form.tokenConfigured ? 'توکن ذخیره شده است؛ برای تغییر وارد کنید' : 'توکن دریافتی از BotFather'} dir="ltr" className="text-left" /><p className="mt-1 text-xs text-steel">توکن قبلی در پنل نمایش داده نمی‌شود.</p></div><div><label className="mb-2 block text-sm font-medium">شناسه چت‌ها یا کانال‌ها</label><Textarea value={form.recipients} onChange={(e) => setForm({ ...form, recipients: e.target.value })} placeholder={'هر شناسه در یک خط؛ نمونه:\n123456789'} rows={6} dir="ltr" className="text-left" /><p className="mt-1 text-xs leading-6 text-steel">ربات را در بله باز کنید و ابتدا /start بفرستید؛ شناسه چت به‌دست‌آمده را هرکدام در یک خط وارد کنید.</p></div><Button type="submit" className="bg-copper" disabled={mutation.isPending}>{mutation.isPending && <Loader2 className="ml-2 h-4 w-4 animate-spin" />}<Save className="ml-2 h-4 w-4" />ذخیره تنظیمات</Button></form></CardContent></Card>;
};

export default Admin;

// ============ Blogs Manager ============
const BlogsManager = () => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<BlogPost | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['admin-blogs'],
    queryFn: () => apiRequest<ApiResponse<BlogPost[]>>(`${apiEndpoints.blogs}?all=true`),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest(`${apiEndpoints.blogs}?id=${id}`, { method: 'DELETE' }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['admin-blogs'] }); toast.success('حذف شد'); },
  });
  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-copper" /></div>;
  if (editing || isCreating) return <BlogForm post={editing} onClose={() => { setEditing(null); setIsCreating(false); }} />;
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>مقالات</CardTitle>
        <Button onClick={() => setIsCreating(true)} className="bg-copper hover:bg-copper-dark"><Plus className="w-4 h-4 ml-2" />افزودن مقاله</Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {(data?.data || []).map((p) => (
            <div key={p.id} className="flex items-center justify-between p-4 bg-steel-light/30 rounded-lg">
              <div className="flex items-center gap-3">
                {p.featured_image ? <img src={resolveApiAssetUrl(p.featured_image) || ''} alt={p.title} className="w-16 h-16 object-cover rounded" /> : <div className="w-16 h-16 bg-copper/10 rounded flex items-center justify-center"><Newspaper className="w-6 h-6 text-copper" /></div>}
                <div>
                  <h3 className="font-bold">{p.title}</h3>
                  <div className="flex gap-2 mt-1 text-xs">
                    <span className={`px-2 py-0.5 rounded ${p.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{p.status === 'published' ? 'منتشر شده' : 'پیش‌نویس'}</span>
                    <span className="text-steel">{new Date(p.publish_date || p.created_at).toLocaleDateString('fa-IR')}</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setEditing(p)}><Edit className="w-4 h-4" /></Button>
                <Button variant="destructive" size="sm" onClick={() => confirm('حذف شود؟') && deleteMutation.mutate(p.id)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
          {(data?.data || []).length === 0 && <p className="text-center text-steel py-8">هنوز مقاله‌ای ثبت نشده</p>}
        </div>
      </CardContent>
    </Card>
  );
};

const BlogForm = ({ post, onClose }: { post: BlogPost | null; onClose: () => void }) => {
  const queryClient = useQueryClient();
  const [f, setF] = useState({
    title: post?.title || '',
    slug: post?.slug || '',
    excerpt: post?.excerpt || '',
    content: post?.content || '',
    featured_image: post?.featured_image || '',
    status: post?.status || 'draft',
    publish_date: post?.publish_date ? post.publish_date.slice(0, 16) : '',
  });
  const mutation = useMutation({
    mutationFn: (d: any) => apiRequest(apiEndpoints.blogs, { method: post ? 'PUT' : 'POST', body: JSON.stringify(post ? { ...d, id: post.id } : d) }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['admin-blogs'] }); toast.success('ذخیره شد'); onClose(); },
  });
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between"><CardTitle>{post ? 'ویرایش مقاله' : 'افزودن مقاله'}</CardTitle><Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button></CardHeader>
      <CardContent>
        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(f); }} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Input placeholder="عنوان *" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="text-right" required />
            <Input placeholder="نامک (slug)" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} dir="ltr" className="text-left" />
          </div>
          <Textarea placeholder="چکیده کوتاه" rows={2} value={f.excerpt} onChange={(e) => setF({ ...f, excerpt: e.target.value })} className="text-right" />
          <div>
            <label className="block text-sm font-medium mb-2">محتوای کامل (HTML)</label>
            <Textarea rows={14} value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} className="text-right font-mono text-sm" placeholder="<p>متن مقاله...</p>" />
            <p className="text-xs text-steel mt-1">پشتیبانی از تگ‌های HTML مانند &lt;p&gt; &lt;h2&gt; &lt;strong&gt; &lt;img&gt; &lt;a&gt; و غیره</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">تصویر شاخص</label>
            <ImageUploader currentImage={f.featured_image || undefined} onUpload={(url) => setF({ ...f, featured_image: url })} />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">وضعیت</label>
              <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value as 'draft' | 'published' })} className="flex h-10 w-full rounded-md border px-3 py-2 text-sm bg-background text-right">
                <option value="draft">پیش‌نویس</option>
                <option value="published">منتشر شده</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">تاریخ انتشار</label>
              <Input type="datetime-local" value={f.publish_date} onChange={(e) => setF({ ...f, publish_date: e.target.value })} dir="ltr" />
            </div>
          </div>
          <Button type="submit" className="bg-copper hover:bg-copper-dark" disabled={mutation.isPending}>{mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : <Save className="w-4 h-4 ml-2" />}ذخیره</Button>
        </form>
      </CardContent>
    </Card>
  );
};

// ============ Messages Manager ============
const MessagesManager = () => {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<ContactMessage | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ['admin-messages'],
    queryFn: () => apiRequest<ApiResponse<ContactMessage[]>>(apiEndpoints.contacts),
  });
  const toggleRead = useMutation({
    mutationFn: (m: ContactMessage) => apiRequest(apiEndpoints.contacts, { method: 'PUT', body: JSON.stringify({ id: m.id, is_read: !m.is_read }) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-messages'] }),
  });
  const del = useMutation({
    mutationFn: (id: number) => apiRequest(`${apiEndpoints.contacts}?id=${id}`, { method: 'DELETE' }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['admin-messages'] }); setSelected(null); toast.success('حذف شد'); },
  });
  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-copper" /></div>;
  const messages = data?.data || [];
  return (
    <Card>
      <CardHeader><CardTitle>پیام‌های تماس ({messages.filter(m => !m.is_read).length} خوانده نشده)</CardTitle></CardHeader>
      <CardContent>
        {selected ? (
          <div className="space-y-4">
            <Button variant="outline" size="sm" onClick={() => setSelected(null)}><X className="w-4 h-4 ml-2" />بازگشت</Button>
            <div className="bg-steel-light/30 p-4 rounded-lg space-y-2">
              <div className="grid md:grid-cols-2 gap-2 text-sm">
                <div><b>نام:</b> {selected.name}</div>
                <div><b>تاریخ:</b> {new Date(selected.created_at).toLocaleString('fa-IR')}</div>
                <div><b>تلفن:</b> <span dir="ltr">{selected.phone || '-'}</span></div>
                <div><b>ایمیل:</b> <span dir="ltr">{selected.email || '-'}</span></div>
                <div className="md:col-span-2"><b>موضوع:</b> {selected.subject || '-'}</div>
              </div>
              <div className="pt-3 border-t">
                <b>پیام:</b>
                <p className="mt-2 whitespace-pre-wrap leading-relaxed">{selected.message}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => toggleRead.mutate(selected)}>{selected.is_read ? <Mail className="w-4 h-4 ml-2" /> : <MailOpen className="w-4 h-4 ml-2" />}{selected.is_read ? 'علامت‌گذاری نخوانده' : 'علامت‌گذاری خوانده'}</Button>
              <Button variant="destructive" onClick={() => confirm('حذف شود؟') && del.mutate(selected.id)}><Trash2 className="w-4 h-4 ml-2" />حذف</Button>
            </div>
          </div>
        ) : messages.length === 0 ? (
          <p className="text-center text-steel py-8">پیامی دریافت نشده است</p>
        ) : (
          <div className="space-y-2">
            {messages.map((m) => (
              <button key={m.id} onClick={() => { setSelected(m); if (!m.is_read) toggleRead.mutate(m); }} className={`w-full text-right flex items-center justify-between p-4 rounded-lg transition-colors ${m.is_read ? 'bg-steel-light/20' : 'bg-copper/10 border border-copper/30'}`}>
                <div className="flex items-center gap-3">
                  {m.is_read ? <MailOpen className="w-5 h-5 text-steel" /> : <Mail className="w-5 h-5 text-copper" />}
                  <div>
                    <p className="font-bold">{m.name} {m.subject && <span className="text-steel font-normal">— {m.subject}</span>}</p>
                    <p className="text-sm text-steel line-clamp-1">{m.message}</p>
                  </div>
                </div>
                <span className="text-xs text-steel">{new Date(m.created_at).toLocaleDateString('fa-IR')}</span>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
